import {
	DebIdFieldConfig,
	type Attribute,
	type FieldProps,
} from "@/types/form-types"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form"
import FieldInfoIcon from "../field-info"
import { Input } from "../ui/input"
import { cn, parseAttributes, toDateSafe } from "@/lib/utils"
import { Description } from "./components/description"
import { Button } from "../ui/button"
import { useDebID } from "@/hooks/use-deb-id"
import { useFormStore } from "@/stores/use-form-store"
import { toast } from "sonner"
import { logger } from "@/utils/logger"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const DebId = ({ form, field, commonAttributes }: FieldProps) => {
	const {
		config,
		verifiedFields,
		applicationId,
		shouldDisable,
		setVerifiedFields,
	} = useFormStore()
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes
	const {
		placeholder,
		// isDisabled,
		abcFieldId,
		dobFieldId,
		emailFieldId,
		genderFieldId,
		mobileFieldId,
		nameFieldId,
	} = getDebIdFieldAttributes(field.attributes)

	const isL1OrL2Verifier = [
		"L1_DOCUMENT_VERIFIER",
		"L2_DOCUMENT_VERIFIER",
	].includes(window.__DF_USER__?.type || "")

	const isVerified =
		(verifiedFields.get(field.field_id) && !isL1OrL2Verifier) || false

	const { useDebIDValidate } = useDebID()
	const { mutateAsync: validateDebID } = useDebIDValidate(
		config?.university_id || "",
		applicationId || "",
	)

	const handleDebIDValidator = async (value: string) => {
		const isValid = await form.trigger(field.field_id)

		if (!isValid) {
			return
		}

		const data = await validateDebID({
			deb_id: String(value),
			application_id: applicationId || "",
			field_id: field.field_id,
			abc_field_id: abcFieldId,
			dob_field_id: dobFieldId,
			email_field_id: emailFieldId,
			gender_field_id: genderFieldId,
			mobile_field_id: mobileFieldId,
			name_field_id: nameFieldId,
		})

		logger.info("deb data: ", data)
		if (data.Status === "Process Success") {
			const studentInfo = data.Resource[0]
			const verifiedDebIdFields: {
				fieldId: string
				isVerified: boolean
			}[] = [{ fieldId: field.field_id, isVerified: true }]

			if (abcFieldId) {
				form.setValue(abcFieldId, studentInfo.ABCID)
				verifiedDebIdFields.push({
					fieldId: abcFieldId,
					isVerified: true,
				})
			}

			if (dobFieldId) {
				const parsed = toDateSafe(studentInfo.DOB, "dd/MM/yyyy")
				logger.info("parsed deb dob", parsed)
				form.setValue(dobFieldId, parsed)
				verifiedDebIdFields.push({
					fieldId: dobFieldId,
					isVerified: true,
				})
			}

			if (emailFieldId) {
				form.setValue(emailFieldId, studentInfo.email)
				verifiedDebIdFields.push({
					fieldId: emailFieldId,
					isVerified: true,
				})
			}

			if (genderFieldId) {
				form.setValue(genderFieldId, studentInfo.Gender.toLowerCase())
				verifiedDebIdFields.push({
					fieldId: genderFieldId,
					isVerified: true,
				})
			}

			if (mobileFieldId) {
				form.setValue(mobileFieldId, "+91" + studentInfo.mobile)
				verifiedDebIdFields.push({
					fieldId: mobileFieldId,
					isVerified: true,
				})
			}

			if (nameFieldId) {
				form.setValue(nameFieldId, studentInfo.stdname)
				verifiedDebIdFields.push({
					fieldId: nameFieldId,
					isVerified: true,
				})
			}
			setVerifiedFields(verifiedDebIdFields)
			toast.success("deb-id verified successfully")
		} else {
			toast.error("invalid deb-id entered")
		}
	}

	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<div className="space-y-3">
							<Input
								disabled={isVerified || shouldDisable}
								{...formField}
								placeholder={placeholder}
							/>

							{!isVerified ? (
								<div>
									<Button
										type="button"
										onClick={() =>
											handleDebIDValidator(
												formField.value,
											)
										}
									>
										verify
									</Button>
								</div>
							) : (
								<p className="text-green-500">verified</p>
							)}
						</div>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getDebIdFieldAttributes(attributes: Attribute[]) {
	const props: {
		placeholder?: string
		abcFieldId?: string
		nameFieldId?: string
		emailFieldId?: string
		mobileFieldId?: string
		genderFieldId?: string
		dobFieldId?: string
		isDisabled: boolean
	} = {
		isDisabled: false,
	}

	const parsedAttributes = parseAttributes(attributes)
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case DebIdFieldConfig.Placeholder:
				props.placeholder = value as string
				break
			case DebIdFieldConfig.ABCFieldId:
				props.abcFieldId = value as string
				break
			case DebIdFieldConfig.DOBFieldId:
				props.dobFieldId = value as string
				break
			case DebIdFieldConfig.EmailFieldId:
				props.emailFieldId = value as string
				break
			case DebIdFieldConfig.GenderFieldId:
				props.genderFieldId = value as string
				break
			case DebIdFieldConfig.IsDisabled:
				props.isDisabled = value as boolean
				break
			case DebIdFieldConfig.MobileFieldId:
				props.mobileFieldId = value as string
				break
			case DebIdFieldConfig.NameFieldId:
				props.nameFieldId = value as string
		}
	})

	return props
}
